import { registerJdid as registerJdidToRum } from '@iheartjane/tracking';
import { useEffect, useMemo } from 'react';
import { useLoaderData } from 'react-router';

import { BloomWizardModal } from '@jane/bloom/components/bloomWizardModal';
import { SeoHeading } from '@jane/bloom/components/seoHeading';
import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { BloomFilterProvider } from '@jane/bloom/hooks/filters';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { RecommendedSortResults } from '@jane/bloom/loaders/products/recommendedSortResults.server';
import { createMetaVariables } from '@jane/bloom/resources/meta/createMetaVariables';
import { DEFAULT_TITLE } from '@jane/bloom/resources/meta/routes/deals';
import { registerJdid } from '@jane/bloom/services/tracking/mixpanel';
import { trackSearchedMenuProducts } from '@jane/bloom/services/tracking/track';
import { isEmpty } from '@jane/bloom/utils/guards/isEmpty';
import { sortSpecials } from '@jane/bloom/utils/specials/sortSpecials';

import { DealsTab } from './components/dealsTab';

export function DealsRouteComponent() {
  const { cart, customSEO, metaProps, pinnedStore } = useRootLoaderData();
  const {
    availableFilters,
    brandSpecials,
    complianceLanguage,
    customLanguage,
    enabledSpecialsForBanner,
    enabledSpecialsForRow: enabledStoreSpecials,
    janeGold,
    jdid,
    menuProductImgsAboveTheFold,
    recommendedSortResults,
    searchCriteria,
    sort: { currentSort, sortOptions },
  } = useLoaderData<RecommendedSortResults>();

  const storeSpecials = useMemo(() => {
    const specials = Object.values(enabledStoreSpecials).flat();

    if (!isEmpty(specials)) {
      return sortSpecials([...specials]);
    }
    return null;
  }, [enabledStoreSpecials]);

  const rowInstance = useMemoizedRowInstance(recommendedSortResults);

  useEffect(() => {
    registerJdid(jdid);
    registerJdidToRum(jdid);
  }, [jdid]);

  useEffect(() => {
    if (!rowInstance) return;
    trackSearchedMenuProducts({
      hits: rowInstance?.totalProducts,
      searchCriteria,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(searchCriteria), rowInstance?.totalProducts]);

  return (
    <BloomFilterProvider
      availableFilters={availableFilters}
      searchCriteria={searchCriteria}
    >
      <SeoHeading
        customSEOPage={customSEO?.deal ?? {}}
        defaultTitle={DEFAULT_TITLE}
        metaVariables={createMetaVariables(metaProps)}
      />

      <BloomWizardModal
        brandSpecials={brandSpecials}
        specials={enabledStoreSpecials}
      >
        {({ openModal }) => (
          <DealsTab
            brandSpecials={brandSpecials}
            customLanguage={customLanguage}
            cart={cart}
            currentSort={currentSort}
            enabledSpecialsForBanner={enabledSpecialsForBanner}
            openWizard={openModal}
            preloadedProductIds={menuProductImgsAboveTheFold.map(
              ({ id }) => id
            )}
            sortOptions={sortOptions}
            rowInstance={rowInstance}
            storeSpecials={storeSpecials}
            complianceLanguage={complianceLanguage}
            janeGold={janeGold}
            pinnedStore={pinnedStore}
          />
        )}
      </BloomWizardModal>
    </BloomFilterProvider>
  );
}
